import m from 'mithril';
import { Store } from '../store';



const SORT_OPTIONS = [
    { id: 'default',    icon: 'lucide:star',                    label: 'Featured',          color: '#eab308' },
    { id: 'price-asc',  icon: 'lucide:arrow-up-narrow-wide',   label: 'Price: Low → High', color: '#22c55e' },
    { id: 'price-desc', icon: 'lucide:arrow-down-wide-narrow', label: 'Price: High → Low', color: '#f97316' },
    { id: 'name',       icon: 'lucide:a-large-small',          label: 'Name A–Z',          color: '#3b82f6' },
];

let hideTimer: ReturnType<typeof setTimeout> | null = null;
const clearHideTimer = () => { if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; } };
const startHideTimer = () => {
    clearHideTimer();
    hideTimer = setTimeout(() => {
        if (!Store.state.sidebarCollapsed) {
            Store.state.sidebarCollapsed = true;
            m.redraw();
        }
    }, 2000);
};

// Section separator — crossfades between labeled and collapsed states, fixed height
const SectionLabel = (title: string, collapsed: boolean) =>
    m('div', { class: 'h-8 relative overflow-hidden' }, [
        // Expanded: accent line + label
        m('div', {
            class: `absolute inset-0 flex items-center gap-2 px-3 transition-opacity duration-500 pointer-events-none
                ${collapsed ? 'opacity-0' : 'opacity-100'}`,
        }, [
            m('span', { class: 'w-2 h-0.5 rounded-full bg-[var(--color-primary)] shrink-0' }),
            m('p', { class: 'text-[10px] font-black tracking-widest uppercase opacity-40 whitespace-nowrap' }, title),
        ]),
        // Collapsed: centered short rule
        m('div', {
            class: `absolute inset-0 flex items-center justify-center transition-opacity duration-500 pointer-events-none
                ${collapsed ? 'opacity-100' : 'opacity-0'}`,
        },
            m('span', { class: 'w-5 h-px rounded-full bg-[var(--color-border)]' })
        ),
    ]);

const row = (icon: string, label: string, active: boolean, collapsed: boolean, color: string, onclick: () => void) =>
    m('div', {
        title: label,
        class: `flex items-center rounded-lg cursor-pointer transition-all duration-300 select-none overflow-hidden
            ${collapsed ? 'mx-0 w-full justify-center rounded-none' : 'mx-2'}
            ${active
                ? 'bg-[var(--color-primary)] text-white shadow-sm'
                : 'opacity-75 hover:opacity-100 hover:bg-[var(--color-border)]'}`,
        onclick,
    }, [
        m('div', { class: 'w-[44px] h-[40px] shrink-0 flex items-center justify-center' },
            icon.includes('<svg')
                ? m('div', {
                    class: 'text-[1.15rem] flex items-center justify-center [&>svg]:w-5 [&>svg]:h-5',
                    style: active ? 'color: white' : `color: ${color}`
                }, m.trust(icon))
                : m('iconify-icon', {
                    icon,
                    class: 'text-[1.15rem]',
                    style: active ? 'color: white' : `color: ${color}`
                })
        ),
        m('div', {
            class: 'overflow-hidden transition-[width,opacity] duration-500 ease-in-out shrink-0',
            style: `width: ${collapsed ? '0px' : '163px'}; opacity: ${collapsed ? '0' : '1'}`,
        }, m('span', { class: 'ps-1 pe-3 text-sm font-semibold whitespace-nowrap block' }, label)),
    ]);

const getSidebarSections = (collapsed: boolean, close: () => void) => {
    const { activeCategory, sortBy, cart } = Store.state;
    
    const renderCategories = () => {
        const dynamicCats = Store.state.categories.map(c => ({
            id: c.slug,
            icon: c.icon || 'lucide:circle',
            label: c.name,
            color: c.color || 'var(--color-primary)'
        }));

        const allCats = [
            { id: 'all', icon: 'lucide:layout-grid', label: 'All Products', color: 'var(--color-primary)' },
            ...dynamicCats
        ];

        return allCats.map(cat => row(
            cat.icon, cat.label, activeCategory === cat.id, collapsed, cat.color,
            () => { 
                Store.state.activeCategory = cat.id; 
                if (m.route.get() !== '/') m.route.set('/');
                if (window.innerWidth < 1024) close(); 
                m.redraw(); 
            }
        ));
    };

    const topSection = [
        // Cart
        m('div', {
            title: 'My Cart',
            class: `flex items-center cursor-pointer transition-all duration-500 overflow-hidden
                bg-[var(--color-primary)] text-white hover:brightness-110 shadow-sm
                ${collapsed ? 'mx-0 w-full justify-center rounded-none' : 'mx-2 rounded-lg'}`,
            onclick: () => { m.route.set('/cart'); close(); },
        }, [
            m('div', { class: 'w-[44px] h-[40px] shrink-0 flex items-center justify-center relative' }, [
                m('iconify-icon', { icon: 'lucide:shopping-bag', class: 'text-[1.15rem]' }),
                cart.length > 0 && m('span', {
                    class: 'absolute top-1.5 end-1.5 bg-white text-[var(--color-primary)] text-[8px] font-black rounded-full w-3.5 h-3.5 flex items-center justify-center leading-none',
                }, cart.length),
            ]),
            m('div', {
                class: 'overflow-hidden transition-[width,opacity] duration-500 ease-in-out shrink-0',
                style: `width: ${collapsed ? '0px' : '163px'}; opacity: ${collapsed ? '0' : '1'}`,
            }, m('span', { class: 'ps-1 pe-3 font-bold text-sm whitespace-nowrap block' }, 'My Cart')),
        ]),

        SectionLabel('Categories', collapsed),
        ...renderCategories(),
    ];

    const bottomSection = [
        SectionLabel('Sort By', collapsed),
        ...SORT_OPTIONS.map(opt => row(
            opt.icon, opt.label, sortBy === opt.id, collapsed, opt.color,
            () => { Store.state.sortBy = opt.id as any; m.redraw(); }
        )),
    ];

    return { topSection, bottomSection };
};

export const SideMenu: m.Component = {
    view() {
        const { isSideMenuOpen, sidebarCollapsed } = Store.state;
        const close = () => { Store.state.isSideMenuOpen = false; m.redraw(); };
        const toggleCollapse = () => {
            Store.state.sidebarCollapsed = !Store.state.sidebarCollapsed;
            clearHideTimer();
            m.redraw();
        };

        const sideW = sidebarCollapsed ? '60px' : '220px';
        const desktopSections = getSidebarSections(sidebarCollapsed, close);
        const mobileSections = getSidebarSections(false, close);

        return m('div', {}, [
            isSideMenuOpen && m('div', {
                class: 'fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden',
                onclick: close,
            }),

            // Desktop sidebar
            m('aside', {
                class: `fixed top-16 start-0 bottom-0 z-30
                    hidden lg:flex flex-col
                    bg-[var(--color-surface)] border-e border-[var(--color-border)]
                    backdrop-blur-xl shadow-lg will-change-[width]`,
                style: `width: ${sideW}; transition: width 500ms cubic-bezier(0.4,0,0.2,1)`,
                onmouseenter: () => {
                    clearHideTimer();
                    if (Store.state.sidebarCollapsed) { Store.state.sidebarCollapsed = false; m.redraw(); }
                },
                onmouseleave: startHideTimer,
            }, [
                // Toggle button — moved to top and aligned to side
                m('div', { class: `h-11 flex items-center shrink-0 border-b border-[var(--color-border)]/30 ${sidebarCollapsed ? 'justify-center' : 'justify-end pe-3'}` }, [
                    m('button', {
                        title: sidebarCollapsed ? 'Expand' : 'Collapse',
                        class: `hover-spring !m-0 !border-0 !bg-transparent !rounded-lg opacity-40 hover:opacity-100 hover:!bg-[var(--color-border)]
                            transition-all duration-300 cursor-pointer shrink-0
                            w-[44px] h-8 flex items-center justify-center`,
                        onclick: toggleCollapse,
                    }, m('iconify-icon', {
                        icon: sidebarCollapsed ? 'lucide:panel-left-open' : 'lucide:panel-left-close',
                        class: 'text-lg',
                    })),
                ]),

                // Scroll — direction:rtl moves bar to LEFT edge
                m('div', {
                    class: 'flex-1 overflow-y-auto overflow-x-hidden sidebar-scroll mt-2',
                    style: 'direction: rtl',
                },
                    m('div', { class: 'py-2 space-y-0.5', style: 'direction: ltr' }, desktopSections.topSection)
                ),

                // Fixed bottom section (Sort By)
                m('div', { class: 'shrink-0 py-2 space-y-0.5 border-t border-[var(--color-border)]/30' },
                    desktopSections.bottomSection
                ),

                // Footer clips away
                m('div', {
                    class: 'overflow-hidden shrink-0 transition-all duration-500 border-t border-[var(--color-border)]/30',
                    style: `max-height: ${sidebarCollapsed ? '0px' : '40px'}; opacity: ${sidebarCollapsed ? '0' : '0.4'}`,
                }, m('p', { class: 'text-[10px] text-center py-3 px-2 whitespace-nowrap' }, '© 2026 KHALID Store')),
            ]),

            // Mobile sidebar
            isSideMenuOpen && m('aside', {
                class: 'fixed top-0 start-0 h-[100dvh] z-50 w-72 flex flex-col lg:hidden shadow-2xl overflow-hidden bg-[var(--color-surface)] border-e border-[var(--color-border)]',
            }, [
                m('div', { class: 'flex items-center justify-between border-b border-[var(--color-border)] px-4 h-14 shrink-0' }, [
                    m('div', { class: 'flex items-center gap-2 text-sm font-black' }, [
                        m('iconify-icon', { icon: 'lucide:sliders-horizontal', class: 'text-[var(--color-primary)]' }),
                        'Browse & Filter',
                    ]),
                    m('button', {
                        class: '!m-0 !p-1.5 !border-0 !bg-transparent !rounded-lg opacity-60 hover:opacity-100 hover:!bg-[var(--color-border)] transition-all cursor-pointer',
                        onclick: close,
                    }, m('iconify-icon', { icon: 'lucide:x', class: 'text-xl' })),
                ]),
                m('div', { class: 'flex-1 overflow-y-auto overflow-x-hidden sidebar-scroll py-2 space-y-0.5' },
                    mobileSections.topSection
                ),
                m('div', { class: 'shrink-0 py-2 space-y-0.5 border-t border-[var(--color-border)]/30' },
                    mobileSections.bottomSection
                ),
                m('div', { class: 'px-4 py-2 border-t border-[var(--color-border)] text-[10px] opacity-25 text-center shrink-0' },
                    '© 2026 KHALID Store'
                ),
            ]),
        ]);
    }
};
